import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import { POSTS, getPostBySlug } from '../content/blog';
import { blogApi, resolveAsset } from '../lib/adminApi';
import { track } from '../lib/analytics';
import useJsonLd from '../lib/useJsonLd';
import { ArrowLeft, ArrowRight, Clock, Linkedin, Loader2 } from 'lucide-react';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
const readMinutes = (words) => Math.max(1, Math.round((words || 220) / 220));

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const seed = getPostBySlug(slug);
  const [remote, setRemote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false); setRemote(null);
    blogApi.get(slug)
      .then((p) => setRemote(p))
      .catch(() => { if (!seed) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug, seed]);

  // Prefer API post over the JSX seed if both exist under the same slug.
  const post = remote
    ? {
        slug: remote.slug, title: remote.title, excerpt: remote.excerpt,
        cover: resolveAsset(remote.cover_url) || (seed && seed.cover) || '',
        category: remote.category,
        date: remote.publish_at || remote.created_at,
        author: { name: remote.author_name, title: remote.author_title || '' },
        words: remote.words,
        html: remote.body_html,
      }
    : seed
      ? {
          slug: seed.slug, title: seed.title, excerpt: seed.excerpt, cover: seed.cover,
          category: seed.category, date: seed.date, author: seed.author, words: seed.words,
          Body: seed.Body,
        }
      : null;

  useEffect(() => {
    if (post) track('blog_post_view', { slug: post.slug, category: post.category });
  }, [post]);

  // Set per-post SEO tags (title, description, canonical, OG). Restore on unmount.
  useEffect(() => {
    if (!post) return undefined;
    const prevTitle = document.title;
    document.title = `${post.title} · 5DBS`;

    const upsertMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      const created = !el;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      const prev = el.getAttribute('content');
      el.setAttribute('content', content || '');
      return () => { if (created) el.remove(); else el.setAttribute('content', prev || ''); };
    };
    const upsertLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
      const prev = el.getAttribute('href');
      el.setAttribute('href', href);
      return () => { if (created) el.remove(); else el.setAttribute('href', prev || ''); };
    };

    const desc = (post.excerpt || post.title || '').slice(0, 300);
    const url = typeof window !== 'undefined' ? `${window.location.origin}/blog/${post.slug}` : `https://fivedbs.com/blog/${post.slug}`;
    const image = post.cover || '';
    const cleanups = [
      upsertMeta('name', 'description', desc),
      upsertLink('canonical', url),
      upsertMeta('property', 'og:title', post.title),
      upsertMeta('property', 'og:description', desc),
      upsertMeta('property', 'og:type', 'article'),
      upsertMeta('property', 'og:url', url),
      upsertMeta('property', 'og:image', image),
      upsertMeta('name', 'twitter:card', 'summary_large_image'),
      upsertMeta('name', 'twitter:title', post.title),
      upsertMeta('name', 'twitter:description', desc),
      upsertMeta('name', 'twitter:image', image),
    ];
    return () => { document.title = prevTitle; cleanups.forEach((fn) => fn && fn()); };
  }, [post]);

  // Extract any FAQPage JSON-LD our backend embedded in body_html so we can
  // (a) re-emit it as a proper <head> node and (b) strip it from the rendered body.
  const { headJsonLd, cleanHtml } = useMemo(() => {
    const html = (post && post.html) || '';
    const scripts = [...html.matchAll(/<script[^>]*application\/ld\+json[^>]*>([\s\S]*?)<\/script>/gi)];
    let faqBlock = null;
    let articleBlock = null;
    for (const m of scripts) {
      try {
        const d = JSON.parse(m[1]);
        if (d && d['@type'] === 'FAQPage') faqBlock = d;
        if (d && d['@type'] === 'Article') articleBlock = d;
      } catch { /* skip malformed */ }
    }
    const stripped = html.replace(/<script[^>]*application\/ld\+json[^>]*>[\s\S]*?<\/script>/gi, '');
    const blocks = [];
    if (post) {
      const url = `https://fivedbs.com/blog/${post.slug}`;
      const image = post.cover || undefined;
      blocks.push({
        '@context': 'https://schema.org',
        '@type': 'Article',
        '@id': `${url}#article`,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        headline: post.title,
        description: post.excerpt || (articleBlock && articleBlock.description) || '',
        image: image ? [image] : undefined,
        datePublished: post.date || undefined,
        dateModified: post.date || undefined,
        author: {
          '@type': 'Person',
          '@id': `${url}#author`,
          name: (post.author && post.author.name) || 'The 5DBS Team',
          jobTitle: (post.author && post.author.title) || undefined,
          worksFor: { '@id': 'https://fivedbs.com/#organization' },
        },
        publisher: { '@id': 'https://fivedbs.com/#organization' },
        keywords: (articleBlock && articleBlock.keywords) || undefined,
        articleSection: post.category || undefined,
        inLanguage: 'en',
      });
      blocks.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://fivedbs.com/' },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://fivedbs.com/blog' },
          { '@type': 'ListItem', position: 3, name: post.title, item: url },
        ],
      });
      if (faqBlock) blocks.push(faqBlock);
    }
    return { headJsonLd: blocks, cleanHtml: stripped };
  }, [post]);

  useJsonLd(headJsonLd);

  if (loading && !seed) {
    return (
      <div className="min-h-screen bg-white font-sans text-navy">
        <Navbar />
        <main className="pt-28 pb-24 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-body-muted" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!post || notFound) {
    return (
      <div className="min-h-screen bg-white font-sans text-navy">
        <Navbar />
        <main className="pt-28 pb-24">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-brand-blue text-[12px] font-bold tracking-[0.16em] uppercase mb-4">404</p>
            <h1 className="text-navy font-black text-[36px]">We couldn&apos;t find that post.</h1>
            <p className="mt-4 text-body-muted">It may have moved, or the link is out of date.</p>
            <button type="button" onClick={() => navigate('/blog')}
              className="mt-8 inline-flex items-center justify-center h-11 px-7 rounded-md bg-brand-pink hover:bg-brand-pink/90 text-white text-[13px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg transition-all duration-300">
              Back to the blog
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const Body = post.Body;
  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/blog/${post.slug}` : `https://fivedbs.com/blog/${post.slug}`;
  const liShare = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-white font-sans text-navy">
      <Navbar />
      <main>
        <section className="pt-28 pb-14 bg-slate-50/60 border-b border-slate-100">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-body-muted hover:text-brand-blue text-[12px] font-bold uppercase tracking-[0.08em] mb-6">
              <ArrowLeft className="h-4 w-4" /> All posts
            </Link>
            <p className="text-brand-pink text-[12px] font-bold uppercase tracking-[0.18em]">{post.category}</p>
            <h1 className="mt-4 text-navy font-black tracking-[-0.01em] leading-[1.08] text-[36px] sm:text-[46px]">{post.title}</h1>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-body-muted text-[13.5px]">
              <span><strong className="text-navy">{post.author.name}</strong>{post.author.title ? ` · ${post.author.title}` : ''}</span>
              <span className="text-slate-300">·</span>
              <span>{formatDate(post.date)}</span>
              <span className="text-slate-300">·</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {readMinutes(post.words)} min read</span>
            </div>
          </div>
        </section>

        {post.cover && (
          <div className="max-w-4xl mx-auto px-6 lg:px-8 -mt-8">
            <img src={post.cover} alt="" className="w-full aspect-[16/9] object-cover object-center rounded-2xl shadow-mk-lg bg-slate-50" loading="eager" />
          </div>
        )}

        <article data-testid="blog-post-body" className="max-w-3xl mx-auto px-6 lg:px-8 mt-14 prose-post">
          {Body ? <Body /> : <div dangerouslySetInnerHTML={{ __html: (cleanHtml || '').replace(/(src|href)="\/files\//g, `$1="${process.env.REACT_APP_BACKEND_URL}/api/files/`) }} />}
        </article>

        <div className="max-w-3xl mx-auto px-6 lg:px-8 mt-14 pt-8 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
          <p className="text-body-muted text-[13px]">Enjoyed this? Share it with a colleague.</p>
          <a href={liShare} target="_blank" rel="noopener noreferrer" data-testid="blog-linkedin-share"
            onClick={() => track('blog_share_click', { slug: post.slug, network: 'linkedin' })}
            className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-brand-blue hover:bg-brand-blue-dark text-white text-[12px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg transition-all duration-300">
            <Linkedin className="h-4 w-4" />
            Share on LinkedIn
          </a>
        </div>

        <section className="mt-20 py-16 bg-navy-deep">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-brand-blue-light text-[11.5px] font-bold uppercase tracking-[0.18em]">Keep going</p>
            <h2 className="mt-3 text-white font-black tracking-[-0.005em] leading-[1.15] text-[28px] md:text-[36px]">Want the full 2026 playbook?</h2>
            <p className="mt-4 text-slate-300 text-[15.5px] leading-[1.7] max-w-2xl mx-auto">Seven revenue plays, a worked margin model, and the ninety-day launch template we use with publishers.</p>
            <Link to="/#playbook" data-testid="blog-cta-playbook"
              className="mt-7 inline-flex items-center justify-center h-11 px-7 rounded-md bg-brand-pink hover:bg-brand-pink/90 text-white text-[13px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg hover:-translate-y-0.5 transition-all duration-300">
              Download the playbook <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </section>

        {related.length > 0 && (
          <section className="py-16">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
              <h3 className="text-navy font-black text-[22px] tracking-tight">Keep reading</h3>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                {related.map((p) => (
                  <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-related-${p.slug}`}
                    className="block rounded-2xl bg-white border border-slate-100 shadow-mk overflow-hidden group hover:shadow-mk-lg hover:-translate-y-1 transition-all duration-500">
                    <div className="aspect-[16/8] overflow-hidden">
                      <img src={p.cover} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                    </div>
                    <div className="p-6">
                      <p className="text-brand-blue text-[11px] font-bold uppercase tracking-[0.18em]">{p.category}</p>
                      <h4 className="mt-2 text-navy font-black text-[19px] leading-tight">{p.title}</h4>
                      <p className="mt-2 text-body-muted text-[14px] leading-[1.65]">{p.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};


export default BlogPost;
